import React, { useEffect, useState } from 'react'
import styles from './ThanksPage.module.css'
import { ThanksPageProps } from './types'
import { isAbTestActive, useAbTestGtm, useAbTestPattern } from '@/components/utils/abTest'
import { ScoutOfferB } from './patterns/ScoutOfferB'
import { ScoutOfferC } from './patterns/ScoutOfferC'
import { ScoutOfferD } from './patterns/ScoutOfferD'
import { LineLinkModal } from './LineLinkModal'
import { useLineLinkModalTrigger } from './LineLinkModal/useLineLinkModalTrigger'

// AGLPサンクスページ経由のPFCV計測用マーケティングコード
const SCOUT_MC_CODE = '16700000000015028300000'

const getPfOrigin = () => {
    const pfDomain = window.ENV?.PF_DOMAIN || window.location.host
    const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:'
    return `${protocol}//${pfDomain}`
}

const ThanksPage: React.FC<ThanksPageProps> = ({
    jobSeeker,
    location = 'kanto',
}) => {
    const [isKuzenLinked, setIsKuzenLinked] = useState<boolean>(false)
    const [encodedEmail, setEncodedEmail] = useState<string>('')

    useEffect(() => {
        if (jobSeeker) {
            setIsKuzenLinked(jobSeeker.kuzen_linked || false)
            setEncodedEmail(jobSeeker.encoded_email || '')
        }
    }, [jobSeeker])

    const formatExpiredDate = (dateString?: string) => {
        if (!dateString) return null
        const date = new Date(dateString)
        const month = date.getMonth() + 1
        const day = date.getDate()
        const hours = date.getHours().toString().padStart(2, '0')
        const minutes = date.getMinutes().toString().padStart(2, '0')
        return { month, day, time: `${hours}:${minutes}` }
    }

    const expiredDate = formatExpiredDate(jobSeeker?.register_token_expired_at)
    const isRegistered = jobSeeker?.id && jobSeeker?.registered
    const hasJobSeeker = jobSeeker?.id

    const getImagePath = (filename: string) => {
        return `/pages/thanks/${filename}`
    }

    // onlineの場合はkantoの画像を使用
    const getLocationForImage = (loc: string) => {
        return loc === 'online' ? 'kanto' : loc
    }

    const getKuzenLinkUrl = () => {
        if (!encodedEmail) return '#'
        return `https://liff.line.me/2007824586-AmeOdXEP?sl=16a5613000&encoded_email=${encodedEmail}&path=lp_ag_thanks`
    }

    // DEV-3392 PF転換バナーのABCDテスト。B〜Dが変わるのはスカウト（PF転換）訴求が出る
    // 「会員登録済みだがPF未転換」の人だけ。それ以外は全パターンでAと同じ表示にする
    const { pattern, isA, isB, isC, isD, isResolved } = useAbTestPattern()
    const { dataViewLabel, dataClickLabel } = useAbTestGtm()
    const isScoutOfferTarget = !!hasJobSeeker && !isRegistered

    // DEV-3392 AGとPFはドメインが違いcookieを共有できないため、PFのAPIでパターンを引換券(ab_token)に換えて
    // PF転換フォームのURLに付ける。パターンをURLに直接載せると、URLを共有された別の人もそのパターンで計測されてしまうため。
    // クリック時に取りに行くとリンクの遷移を止めることになりGTMのクリック計測に響くため、表示時に取っておく。
    // 失敗したら引換券なしで遷移する（パターンが引き継がれないだけ）
    const [abToken, setAbToken] = useState<string | null>(null)
    useEffect(() => {
        if (!isResolved || !isScoutOfferTarget || !isAbTestActive(window.location.pathname)) return

        fetch(`${getPfOrigin()}/api/v1/ab_test_handoffs`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ issue_number: '3392', pattern }),
        })
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => setAbToken(data?.token ?? null))
            .catch(() => setAbToken(null))
    }, [isResolved, isScoutOfferTarget, pattern])

    const getUpgradeUrl = () => {
        if (!jobSeeker?.register_token) return '#'
        const url = `${getPfOrigin()}/job_seekers/${jobSeeker.register_token}/edit?mc=${SCOUT_MC_CODE}`
        return abToken ? `${url}&ab_token=${encodeURIComponent(abToken)}` : url
    }
    const isPatternLayout = isScoutOfferTarget && !isA
    // KUZEN連携済みの人にはAでもLINE連携バナーを出していないため、モーダルも出さない。
    // isKuzenLinked/encodedEmailのstateはマウント直後に一度falseになるため、propsを直接見る
    const isLineLinkModalEnabled =
        isPatternLayout &&
        !jobSeeker?.kuzen_linked &&
        !!jobSeeker?.encoded_email
    const lineLinkModal = useLineLinkModalTrigger(isLineLinkModalEnabled)

    const telImageSrcSp = getImagePath(
        `entry_thanks_tel_${getLocationForImage(location)}${isKuzenLinked ? '' : '_no_id'}_sp.png`,
    )

    const thanksPageGtmId =
        (typeof window !== 'undefined' && window.ENV?.GTM_ID) || 'GTM-5K6833JW'

    useEffect(() => {
        const gtmId = window.ENV?.GTM_ID || 'GTM-5K6833JW'
        const w = window as Window & { dataLayer?: unknown[] }
        w.dataLayer = w.dataLayer || []
        w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
        const script = document.createElement('script')
        script.async = true
        script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`
        document.head.appendChild(script)
    }, [])

    // パターン確定前はAの表示で一瞬描画され、A側のdata-view-labelが誤発火しうるため何も出さない（DEV-3379と同じ考え方）
    if (!isResolved) return <div className={styles.thanksBody} />

    const patternProps = {
        telImageSrc: telImageSrcSp,
        upgradeUrl: getUpgradeUrl(),
        expiredDate,
    }

    return (
        <div className={styles.thanksBody}>
            <noscript>
                <iframe
                    src={`https://www.googletagmanager.com/ns.html?id=${thanksPageGtmId}`}
                    height={0}
                    width={0}
                    style={{ display: 'none', visibility: 'hidden' }}
                    title="Google Tag Manager"
                />
            </noscript>
            <header
                data-view-label={dataViewLabel('Thanks_HD_離脱POP')}
                className={styles.thanksHeader}>
                <img src='/pages/logo/logo.png' alt='foodsLabo' />
            </header>

            <div className={styles.thanksWrap}>
                {isPatternLayout ? (
                    <>
                        {isB && <ScoutOfferB {...patternProps} />}
                        {isC && <ScoutOfferC {...patternProps} />}
                        {isD && <ScoutOfferD {...patternProps} />}
                    </>
                ) : (
                    <section className={styles.thanksWrapSection}>
                        <div style={{ padding: '0 20px' }}>
                            <img
                                src={getImagePath('entry_thanks_text.png')}
                                alt=''
                                className={styles.entryThanksText}
                            />
                        </div>

                        {isKuzenLinked ? (
                            <>
                                <div className={styles.thanksContent}>
                                    <img
                                        src={getImagePath(
                                            `entry_thanks_tel_${getLocationForImage(location)}_pc.png`,
                                        )}
                                        alt=''
                                        className={styles.entryThanksTelPc}
                                    />
                                </div>
                                <div className={styles.thanksContent}>
                                    <img
                                        src={getImagePath(
                                            `entry_thanks_tel_${getLocationForImage(location)}_sp.png`,
                                        )}
                                        alt=''
                                        className={styles.entryThanksTelSp}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div data-view-label={dataViewLabel('Thanks_CT_LINE-連携')}>
                                    <a
                                        href={getKuzenLinkUrl()}
                                        data-click-label={dataClickLabel('Thanks_CT_LINE-連携_CTAボタン')}
                                    >
                                        <div className={styles.thanksContent}>
                                            <img
                                                src={getImagePath(
                                                    'kuzen-foodslabo-thanks-banner-pc.png',
                                                )}
                                                alt='KUZEN ID連携バナー'
                                                className={styles.entryThanksTelPc}
                                            />
                                        </div>
                                        <div className={styles.thanksContent}>
                                            <img
                                                src={getImagePath(
                                                    'kuzen-foodslabo-thanks-banner-sp.png',
                                                )}
                                                alt='KUZEN ID連携バナー'
                                                className={styles.entryThanksTelSp}
                                            />
                                        </div>
                                    </a>
                                </div>
                                <div className={styles.thanksContent}>
                                    <img
                                        src={getImagePath(
                                            `entry_thanks_tel_${getLocationForImage(location)}_no_id_pc.png`,
                                        )}
                                        alt='TEL画像'
                                        className={styles.entryThanksTelPc}
                                    />
                                </div>
                                <div className={styles.thanksContent}>
                                    <img
                                        src={getImagePath(
                                            `entry_thanks_tel_${getLocationForImage(location)}_no_id_sp.png`,
                                        )}
                                        alt='TEL画像'
                                        className={`${styles.entryThanksTelSp} ${styles.entryThanksTelNoIdSp}`}
                                    />
                                </div>
                            </>
                        )}

                        {isRegistered ? (
                            <div className={styles.entryThanksBottomContents}>
                                <img
                                    src={getImagePath(
                                        'entry_thanks_bottom_contents.png',
                                    )}
                                    alt=''
                                />
                            </div>
                        ) : hasJobSeeker ? (
                            <div
                                className={styles.specialOfferMailWrap}
                                data-view-label={dataViewLabel('Thanks_CT_スカウト')}
                                style={{ marginTop: '10px' }}
                            >
                                <div
                                    className={
                                        styles.specialOfferMailWrapBackgroundImg
                                    }
                                >
                                    <div
                                        className={
                                            styles.specialOfferMailWrapContentWrap
                                        }
                                    >
                                        {/* DEV-3392: スカウト訴求の上下で計測を分けるため、上部(オファー見出し)をコンテンツ1とする */}
                                        <div data-view-label={dataViewLabel('Thanks_CT_スカウト_コンテンツ1')}>
                                            <h1>
                                                <img
                                                    src='https://foodslabo-app-prod.s3.ap-northeast-1.amazonaws.com/static/mail/pf_special-offer_ttl.png'
                                                    alt='スカウト転職へのスペシャルオファー'
                                                />
                                            </h1>
                                            {expiredDate && (
                                                <div className={styles.offerEndText}>
                                                    <p>あなたのオファー終了日</p>
                                                    <h2>
                                                        <span>{expiredDate.month}</span>
                                                        月<span>{expiredDate.day}</span>
                                                        日
                                                        <span>{expiredDate.time}</span>
                                                    </h2>
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles.upgradeBtnWrap}>
                                            <a
                                                href={getUpgradeUrl()}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                data-click-label={dataClickLabel('Thanks_CT_スカウト_CTAボタン1')}
                                            >
                                                今すぐスカウトを受け取る
                                                <svg
                                                    style={{ marginRight: 0 }}
                                                    width='16'
                                                    height='16'
                                                    viewBox='0 0 16 16'
                                                    fill='none'
                                                    xmlns='http://www.w3.org/2000/svg'
                                                >
                                                    <path
                                                        d='M4.99986 15C4.74398 15 4.48797 14.9023 4.29297 14.707C3.90234 14.3164 3.90234 13.6836 4.29297 13.2929L9.58741 7.99992L4.29297 2.70705C3.90234 2.31642 3.90234 1.6836 4.29297 1.29297C4.6836 0.902343 5.31642 0.902343 5.70705 1.29297L11.7071 7.29304C12.0977 7.68367 12.0977 8.31649 11.7071 8.70712L5.70705 14.7072C5.51236 14.9031 5.25611 15 4.99986 15Z'
                                                        fill='#FFFFFF'
                                                        stroke='#FFFFFF'
                                                        strokeLinejoin='round'
                                                    />
                                                </svg>
                                            </a>
                                        </div>
                                        <div className={styles.upgradeBenefitsWrap}>
                                            <img
                                                src='https://foodslabo-app-prod.s3.ap-northeast-1.amazonaws.com/static/mail/upgrade_benefit_1.png'
                                                alt=''
                                            />
                                        </div>
                                        <div className={styles.upgradeBenefitsWrap}>
                                            <img
                                                src='https://foodslabo-app-prod.s3.ap-northeast-1.amazonaws.com/static/mail/upgrade_benefit_2.png'
                                                alt=''
                                            />
                                        </div>
                                        <div className={styles.upgradeBenefitsWrap}>
                                            <img
                                                src='https://foodslabo-app-prod.s3.ap-northeast-1.amazonaws.com/static/mail/upgrade_benefit_3.png'
                                                alt=''
                                            />
                                        </div>
                                        {/* DEV-3392: 「アップグレードするには?」をコンテンツ2とする */}
                                        <div
                                            className={styles.upgradeBenefitsWrap}
                                            data-view-label={dataViewLabel('Thanks_CT_スカウト_コンテンツ2')}
                                        >
                                            <img
                                                src='https://foodslabo-app-prod.s3.ap-northeast-1.amazonaws.com/static/mail/upgrade_benefit_4.png'
                                                alt=''
                                            />
                                        </div>
                                        <div className={styles.upgradeBtnWrap}>
                                            <a
                                                href={getUpgradeUrl()}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                data-click-label={dataClickLabel('Thanks_CT_スカウト_CTAボタン2')}
                                            >
                                                今すぐスカウトを受け取る
                                                <svg
                                                    style={{ marginRight: 0 }}
                                                    width='16'
                                                    height='16'
                                                    viewBox='0 0 16 16'
                                                    fill='none'
                                                    xmlns='http://www.w3.org/2000/svg'
                                                >
                                                    <path
                                                        d='M4.99986 15C4.74398 15 4.48797 14.9023 4.29297 14.707C3.90234 14.3164 3.90234 13.6836 4.29297 13.2929L9.58741 7.99992L4.29297 2.70705C3.90234 2.31642 3.90234 1.6836 4.29297 1.29297C4.6836 0.902343 5.31642 0.902343 5.70705 1.29297L11.7071 7.29304C12.0977 7.68367 12.0977 8.31649 11.7071 8.70712L5.70705 14.7072C5.51236 14.9031 5.25611 15 4.99986 15Z'
                                                        fill='#FFFFFF'
                                                        stroke='#FFFFFF'
                                                        strokeLinejoin='round'
                                                    />
                                                </svg>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div
                                className={styles.entryThanksBottomContentsDefault}
                            >
                                <img
                                    src={getImagePath(
                                        'entry_thanks_bottom_contents.png',
                                    )}
                                    alt=''
                                />
                            </div>
                        )}
                    </section>
                )}

                <footer className={styles.thanksFooter}>
                    <small>
                        厚生労働大臣許可番号有料職業紹介事業許可番号13-ユ-308506
                        <br />
                        Copyright &copy; {new Date().getFullYear()} foodsLabo by
                        cuolega All rights reserved.
                    </small>
                    <div>
                        <a href='https://cuolega.co.jp/privacy/'>
                            プライバシーポリシー
                        </a>
                        <a href='https://cuolega.co.jp/terms/'>利用規約</a>
                        <a href='https://cuolega.co.jp/profile/'>会社概要</a>
                    </div>
                </footer>
            </div>

            {lineLinkModal.isOpen && (
                <LineLinkModal lineUrl={getKuzenLinkUrl()} onClose={lineLinkModal.close} />
            )}
        </div>
    )
}

export default ThanksPage
